import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { http, money, maskMobile } from "../api";

/**
 * Header-level global search that opens a floating result panel.
 * Powered by /api/search (scoped server-side).
 */
export default function GlobalSearch({ onSelectLead }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ leads: [], applications: [] });
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef();

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults({ leads: [], applications: [] });
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await http.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(r.data);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = (leadId) => {
    onSelectLead?.(leadId);
    setOpen(false);
    setQ("");
  };

  const total = results.leads.length + results.applications.length;

  return (
    <div className="global-search-wrap" ref={boxRef}>
      <div className="global-search">
        <Search size={16} />
        <input
          data-testid="global-search-input"
          placeholder="Search leads, mobile, PAN, application…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q && setOpen(true)}
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div className="search-panel" data-testid="global-search-panel">
          {loading ? (
            <div className="search-empty">Searching…</div>
          ) : total === 0 ? (
            <div className="search-empty">No matches within your access boundary.</div>
          ) : (
            <>
              {results.leads.length > 0 && (
                <>
                  <p className="search-heading">Login Status</p>
                  {results.leads.map((l) => (
                    <button
                      key={l.lead_id}
                      className="search-item"
                      data-testid={`search-lead-${l.lead_id}`}
                      onClick={() => pick(l.lead_id)}
                    >
                      <div>
                        <strong>{l.customer_name}</strong>
                        <span className="cell-sub">
                          {l.lead_id} · {maskMobile(l.mobile)}
                        </span>
                      </div>
                      <div className="search-right">
                        <b>{money(l.loan_amount)}</b>
                        <small>
                          {l.loan_type} · {l.status}
                        </small>
                      </div>
                    </button>
                  ))}
                </>
              )}
              {results.applications.length > 0 && (
                <>
                  <p className="search-heading">Applications</p>
                  {results.applications.map((a) => (
                    <button
                      key={a.application_id}
                      className="search-item"
                      data-testid={`search-application-${a.application_id}`}
                      onClick={() => pick(a.lead_id)}
                    >
                      <div>
                        <strong>{a.customer_name}</strong>
                        <span className="cell-sub">
                          {a.application_id} · {a.bank_name} · {a.product_name}
                        </span>
                      </div>
                      <div className="search-right">
                        <b>{money(a.requested_amount)}</b>
                        <small>{a.status}</small>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
